/**
 * **Move to** for a board (spec M1-B1 §7.4 entry 11, §5.3 `boards.move`).
 *
 * monday's `Move to ▸` is a flyout, but a flyout cannot show a folder tree of a 404-board
 * workspace, and the destination is a *pair* — workspace **and** folder — which a single
 * column of menu items cannot express. So the menu entry opens this dialog: pick the
 * workspace, then a folder inside it (or its root). The move is one `boards.move` call, and
 * the service is what refuses an impossible destination.
 *
 * The folder list comes from the sidebar query for the **selected** workspace, which is
 * already cached for the current one and is a single round trip for any other.
 *
 * **P-091 (round 10e).** Two things. The dialog is now reachable from the board `···` as well
 * as from the sidebar row menu — one dialog, two entry points — and it **refetches the
 * workspace list when it opens**: `useWorkspaceList` holds its answer for 30 s, so a workspace
 * created moments earlier (by this user in another tab, by a teammate, or by the migration
 * importer) was simply missing from the destination picker. A destination picker is the one
 * place where a stale list is a wrong answer rather than a slow one.
 */
import { useEffect, useState } from 'react';
import { useToast, useOverlayDismiss } from '@elizmonday/ui';
import { useSidebarData, useWorkspaceList } from '../shell/bootstrap';
import { useBoard, useBoardMutations } from './hooks';
import './boards.css';

export function MoveBoardDialog({ boardId, onClose }: { boardId: string; onClose(): void }) {
  /** P-260: Escape closes it, and focus returns to the menu row that opened it. */
  useOverlayDismiss({ onClose });

  const board = useBoard(boardId);
  const workspaces = useWorkspaceList('');
  const mutations = useBoardMutations();
  const toast = useToast();

  /** P-091: the destination list must be current, not up to 30 s old. */
  const refetchWorkspaces = workspaces.refetch;
  useEffect(() => {
    void refetchWorkspaces();
  }, [refetchWorkspaces]);

  const currentWorkspaceId = board.data?.workspaceId ?? null;
  const [workspaceId, setWorkspaceId] = useState<string | null>(currentWorkspaceId);
  const [folderId, setFolderId] = useState<string | null>(board.data?.folderId ?? null);
  const [saving, setSaving] = useState(false);

  // The board query resolves after the first render; adopt its answer once.
  useEffect(() => {
    if (workspaceId === null && currentWorkspaceId !== null) setWorkspaceId(currentWorkspaceId);
  }, [currentWorkspaceId, workspaceId]);

  const sidebar = useSidebarData(workspaceId);
  const folders = sidebar.data?.folders ?? [];

  const unchanged =
    workspaceId === currentWorkspaceId && (folderId ?? null) === (board.data?.folderId ?? null);

  const submit = async () => {
    if (!workspaceId || unchanged) return;
    setSaving(true);
    try {
      // A dialog that fires a mutation and closes leaves the POST in flight; a reload aborts
      // it (round-3 defect 5). Await the server, then close.
      await mutations.move.mutateAsync({ id: boardId, workspaceId, folderId });
      toast(`${board.data?.name ?? 'Board'} moved`, { kind: 'positive' });
      onClose();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'The board could not be moved', {
        kind: 'negative',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ez-dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        className="ez-board-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Move board"
        data-testid="move-board-dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="ez-board-dialog__title">Move board</h2>

        <label className="ez-board-dialog__field">
          <span>Workspace</span>
          <select
            data-testid="move-workspace"
            value={workspaceId ?? ''}
            onChange={(event) => {
              setWorkspaceId(event.target.value);
              // A folder belongs to one workspace: changing it invalidates the choice.
              setFolderId(null);
            }}
          >
            {(workspaces.data?.all ?? []).map((workspace) => (
              <option key={workspace.id} value={workspace.id}>
                {workspace.name}
              </option>
            ))}
          </select>
        </label>

        <label className="ez-board-dialog__field">
          <span>Folder</span>
          <select
            data-testid="move-folder"
            value={folderId ?? ''}
            onChange={(event) => setFolderId(event.target.value === '' ? null : event.target.value)}
          >
            <option value="">No folder (workspace root)</option>
            {folders.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.name}
              </option>
            ))}
          </select>
        </label>

        <footer className="ez-board-dialog__footer">
          <button type="button" className="ez-board-dialog__cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ez-board-dialog__save"
            data-testid="move-board-submit"
            disabled={unchanged || saving || !workspaceId}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Move'}
          </button>
        </footer>
      </div>
    </div>
  );
}
